import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useSnackbar } from '@/hooks/useSnackbar'
import { DisableIntakeForm, EnableIntakeForm } from '@/services/IntakeFormService'

interface SetIntakeFormEnabledVariables {
  orgId: string
  baseFormId: string
  enabled: boolean
  /** Reported once the change lands, so the wording stays with the tab that owns it. */
  successMessage: string
}

/**
 * Switch one intake form on or off for patients.
 *
 * The direction travels with the request rather than being a flip of what the
 * screen last showed, so a stale screen still applies what the operator chose.
 */
export const useSetIntakeFormEnabled = () => {
  const queryClient = useQueryClient()
  const { showSuccess } = useSnackbar()

  return useMutation({
    mutationFn: ({ orgId, baseFormId, enabled }: SetIntakeFormEnabledVariables) =>
      enabled ? EnableIntakeForm(orgId, baseFormId) : DisableIntakeForm(orgId, baseFormId),
    onSuccess: (_form, { successMessage }) => {
      showSuccess(successMessage)
    },
    // Re-read on failure too: a timeout or server error can follow a change that landed.
    onSettled: (_form, _error, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['intakeForms', orgId] })
    },
  })
}
